import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import FacilityNetworkBackButton from "./FacilityNetworkBackButton";
import FacilityNetworkFigmaCard from "./FacilityNetworkFigmaCard";
import {
  FQHC_DASHBOARD_SUBTITLE,
  FQHC_ROSTER_SUBTITLE,
  FQHC_TYPE_CODE,
  buildFqhcMembershipSignature,
  buildFqhcSourceTooltip,
  buildLiveFqhcDashboardModel,
  collectLinkedFqhcFacilities,
  formatFqhcCenterType,
  formatFqhcFtcaStatus,
  formatFqhcFtcaStatusReason,
  formatFqhcNumber,
  formatFqhcPoints,
  formatFqhcRegistration,
  fqhcToneColor,
  type FqhcKpiTile,
  type FqhcLiveRosterRow,
} from "./facilityNetworkFqhcDashboardUtils";
import {
  getNetworkFqhc,
  getNetworkFqhcForNetwork,
  type FqhcDashboardData,
  type FqhcDashboardSort,
  type FqhcSourceProgram,
  type NetworkDashboardMeta,
} from "@services/networkDashboard";
import { FN } from "./facilityNetworkTheme";

type Props = {
  members: FacilityHierarchyInput[];
  selectedFacilityId: number | null;
  onBack: () => void;
  onOpenFacility?: (facility: MyFacilityListRow) => void;
  onOpenNetworkFacility?: (ccn: string) => void;
  networkId?: number | null;
};

const STATUS_NEUTRAL = "#605e5c";
const VALUE_NEUTRAL = "#323130";
const DEFAULT_PAGE_SIZE = 25;

const KPI_SOURCES: Record<string, FqhcSourceProgram[]> = {
  grants: ["hrsa_active_grants"],
  cqm: ["hrsa_uds_h80", "hrsa_uds_lal"],
  chqr: ["hrsa_chqr"],
  ftca: ["hrsa_ftca"],
};

function SourceTooltip({
  title,
  children,
}: {
  title: string;
  children: React.ReactElement;
}) {
  if (!title) return children;
  return (
    <Tooltip
      title={title}
      componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
      arrow
    >
      {children}
    </Tooltip>
  );
}

function KpiTile({
  tile,
  sourceTooltip,
}: {
  tile: FqhcKpiTile;
  sourceTooltip?: string;
}) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: { xs: "100%", sm: "calc(50% - 4px)", md: 0 },
        bgcolor: "#fff",
        border: "1px solid #e5e5e5",
        borderRadius: "4px",
        boxShadow:
          "1px 1px 2px rgba(98,102,104,0.05), -1px -1px 2px rgba(98,102,104,0.05)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <SourceTooltip title={sourceTooltip ?? ""}>
        <Typography
          component="span"
          tabIndex={sourceTooltip ? 0 : undefined}
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 14,
            fontWeight: 600,
            color: STATUS_NEUTRAL,
            lineHeight: "20px",
            textDecoration: sourceTooltip ? "underline dotted" : "none",
            textUnderlineOffset: "3px",
            width: "fit-content",
          }}
        >
          {tile.label}
        </Typography>
      </SourceTooltip>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 24,
          fontWeight: 600,
          color: tile.valueTone ? fqhcToneColor(tile.valueTone) : VALUE_NEUTRAL,
          letterSpacing: FN.figmaTitleTracking,
          lineHeight: "32px",
        }}
      >
        {tile.value}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          color: STATUS_NEUTRAL,
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        {tile.sublabel}
      </Typography>
    </Box>
  );
}

function SortableHeader({
  label,
  column,
  sort,
  direction,
  onSort,
  sourceTooltip = "",
  sx,
}: {
  label: string;
  column: FqhcDashboardSort;
  sort: FqhcDashboardSort;
  direction: "asc" | "desc";
  onSort: (column: FqhcDashboardSort) => void;
  sourceTooltip?: string;
  sx?: Record<string, unknown>;
}) {
  const active = sort === column;
  return (
    <TableCell
      sortDirection={active ? direction : false}
      sx={{
        fontFamily: FN.fontFamily,
        fontSize: 14,
        fontWeight: 600,
        color: STATUS_NEUTRAL,
        bgcolor: "#fafafa",
        borderColor: "#e5e5e5",
        py: 1,
        px: 1.5,
        ...sx,
      }}
    >
      <SourceTooltip title={sourceTooltip}>
        <TableSortLabel
          active={active}
          direction={active ? direction : "asc"}
          onClick={() => onSort(column)}
        >
          {label}
        </TableSortLabel>
      </SourceTooltip>
    </TableCell>
  );
}

function SourceHeader({
  label,
  sourceTooltip,
  sx,
}: {
  label: string;
  sourceTooltip: string;
  sx: Record<string, unknown>;
}) {
  return (
    <TableCell sx={sx}>
      <SourceTooltip title={sourceTooltip}>
        <Box
          component="span"
          tabIndex={sourceTooltip ? 0 : undefined}
          sx={{
            textDecoration: sourceTooltip ? "underline dotted" : "none",
            textUnderlineOffset: "3px",
          }}
        >
          {label}
        </Box>
      </SourceTooltip>
    </TableCell>
  );
}

function LiveValue({
  primary,
  secondary,
}: {
  primary: string;
  secondary?: string | null;
}) {
  return (
    <>
      <Typography sx={{ fontSize: 12, color: VALUE_NEUTRAL }}>
        {primary}
      </Typography>
      {secondary ? (
        <Typography sx={{ fontSize: 11, color: STATUS_NEUTRAL }}>
          {secondary}
        </Typography>
      ) : null}
    </>
  );
}

function humanizeSourceName(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function LiveRosterTable({
  rows,
  sort,
  direction,
  onSort,
  meta,
  onOpenFacility,
  onOpenNetworkFacility,
}: {
  rows: FqhcLiveRosterRow[];
  sort: FqhcDashboardSort;
  direction: "asc" | "desc";
  onSort: (column: FqhcDashboardSort) => void;
  meta: NetworkDashboardMeta;
  onOpenFacility?: (facility: MyFacilityListRow) => void;
  onOpenNetworkFacility?: (ccn: string) => void;
}) {
  const headerSx = {
    fontFamily: FN.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: STATUS_NEUTRAL,
    bgcolor: "#fafafa",
    borderColor: "#e5e5e5",
    py: 1,
    px: 1.5,
  };
  const cellSx = {
    fontFamily: FN.fontFamily,
    borderColor: "#e5e5e5",
    py: 1.5,
    px: 1.5,
    verticalAlign: "top",
  };
  const sourceTooltip = (...sources: FqhcSourceProgram[]) =>
    buildFqhcSourceTooltip(meta, sources);

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 1700 }}>
        <TableHead>
          <TableRow>
            <SortableHeader
              label="Facility"
              column="name"
              sort={sort}
              direction={direction}
              onSort={onSort}
              sourceTooltip={sourceTooltip("hrsa_hc_sites")}
              sx={{ minWidth: 260 }}
            />
            <SourceHeader
              label="Center type"
              sourceTooltip={sourceTooltip("hrsa_hc_sites")}
              sx={headerSx}
            />
            <SortableHeader
              label="Grant / designation expiration"
              column="grant_expiration"
              sort={sort}
              direction={direction}
              onSort={onSort}
              sourceTooltip={sourceTooltip("hrsa_active_grants")}
              sx={{ minWidth: 180 }}
            />
            <SortableHeader
              label="CHQR"
              column="chqr_badges"
              sort={sort}
              direction={direction}
              onSort={onSort}
              sourceTooltip={sourceTooltip("hrsa_chqr")}
            />
            <SortableHeader
              label="CQM delta"
              column="cqm_delta"
              sort={sort}
              direction={direction}
              onSort={onSort}
              sourceTooltip={sourceTooltip("hrsa_uds_h80", "hrsa_uds_lal")}
              sx={{ minWidth: 150 }}
            />
            <SortableHeader
              label="FTCA"
              column="ftca_status"
              sort={sort}
              direction={direction}
              onSort={onSort}
              sourceTooltip={sourceTooltip("hrsa_ftca")}
              sx={{ minWidth: 150 }}
            />
            <SourceHeader
              label="340B"
              sourceTooltip={sourceTooltip("hrsa_340b")}
              sx={headerSx}
            />
            <SourceHeader
              label="Pharmacies"
              sourceTooltip={sourceTooltip("hrsa_340b")}
              sx={headerSx}
            />
            <SortableHeader
              label="Active sites"
              column="sites"
              sort={sort}
              direction={direction}
              onSort={onSort}
              sourceTooltip={sourceTooltip("hrsa_hc_sites")}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const dashboardFacility = row.dashboardFacility;
            const location = [row.city, row.state].filter(Boolean).join(", ");
            const facilityMeta = [
              row.ccn ? `CCN ${row.ccn}` : null,
              location || null,
              row.health_center_key,
            ]
              .filter(Boolean)
              .join(" · ");
            const expirationReason = row.expiration_availability_reason
              ? humanizeSourceName(row.expiration_availability_reason)
              : null;
            const ftcaReason = formatFqhcFtcaStatusReason(
              row.ftca_status_reason,
            );
            const ftcaTooltip = [
              sourceTooltip("hrsa_ftca"),
              ftcaReason,
            ]
              .filter(Boolean)
              .join("\n");
            const missingSources = row.missing_sources.length
              ? `Missing: ${row.missing_sources.map(humanizeSourceName).join(", ")}`
              : null;
            const canOpenLocal = Boolean(dashboardFacility && onOpenFacility);
            const canOpenNetwork = Boolean(row.ccn && onOpenNetworkFacility);
            return (
              <TableRow
                key={row.key}
                hover
                sx={{ "&:hover": { bgcolor: "#f3fdff" } }}
              >
                <TableCell sx={cellSx}>
                  {canOpenLocal || canOpenNetwork ? (
                    <Button
                      variant="text"
                      aria-label={`Open ${row.name ?? "facility"} dashboard`}
                      onClick={() => {
                        if (dashboardFacility && onOpenFacility) {
                          onOpenFacility(dashboardFacility);
                        } else if (row.ccn && onOpenNetworkFacility) {
                          onOpenNetworkFacility(row.ccn);
                        }
                      }}
                      sx={{
                        minWidth: 0,
                        p: 0,
                        justifyContent: "flex-start",
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {row.name ?? "—"}
                    </Button>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: VALUE_NEUTRAL }}>
                      {row.name ?? "—"}
                    </Typography>
                  )}
                  {facilityMeta ? (
                    <Typography sx={{ fontSize: 11, color: STATUS_NEUTRAL }}>
                      {facilityMeta}
                    </Typography>
                  ) : null}
                  {row.identity_status === "unmatched" ? (
                    <Typography sx={{ fontSize: 11, color: "#8b4409" }}>
                      HRSA identity unmatched
                    </Typography>
                  ) : null}
                  {row.identity_status === "ambiguous" ? (
                    <Typography sx={{ fontSize: 11, color: "#a4262c" }}>
                      HRSA identity ambiguous
                    </Typography>
                  ) : null}
                  {missingSources ? (
                    <Typography sx={{ fontSize: 11, color: "#8b4409" }}>
                      {missingSources}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue primary={formatFqhcCenterType(row.center_type)} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue
                    primary={row.grant_or_designation_expires_on ?? "—"}
                    secondary={expirationReason}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue
                    primary={formatFqhcNumber(row.chqr_badge_count)}
                    secondary={
                      row.chqr_cycle_year == null
                        ? null
                        : `Cycle ${row.chqr_cycle_year}`
                    }
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue
                    primary={formatFqhcPoints(row.cqm_delta_points)}
                    secondary={[
                      row.cqm_reporting_year == null
                        ? null
                        : String(row.cqm_reporting_year),
                      row.cqm_measure_count == null
                        ? null
                        : `${row.cqm_measure_count} measures`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  <SourceTooltip title={ftcaTooltip}>
                    <Box
                      tabIndex={ftcaTooltip ? 0 : undefined}
                      sx={{
                        width: "fit-content",
                        textDecoration: ftcaReason ? "underline dotted" : "none",
                        textUnderlineOffset: "3px",
                      }}
                    >
                      <LiveValue
                        primary={formatFqhcFtcaStatus(row.ftca_status)}
                        secondary={
                          row.ftca_expires_on
                            ? `Expires ${row.ftca_expires_on}`
                            : null
                        }
                      />
                    </Box>
                  </SourceTooltip>
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue primary={formatFqhcRegistration(row.is_340b_registered)} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue primary={formatFqhcNumber(row.contract_pharmacy_count)} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <LiveValue primary={formatFqhcNumber(row.active_site_count)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

type LiveLoadState =
  | { scopeKey: string; status: "idle" | "loading" }
  | {
      scopeKey: string;
      status: "loaded";
      data: FqhcDashboardData;
      meta: NetworkDashboardMeta;
    }
  | { scopeKey: string; status: "error"; message: string };

type FqhcQuery = {
  scopeKey: string;
  page: number;
  pageSize: number;
  sort: FqhcDashboardSort;
  direction: "asc" | "desc";
};

function defaultQuery(scopeKey: string): FqhcQuery {
  return {
    scopeKey,
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: "grant_expiration",
    direction: "asc",
  };
}

/** Figma 9120:91315 — FQHC type-detail dashboard with optional live API mode. */
export default function FacilityNetworkFqhcDashboardView({
  members,
  selectedFacilityId,
  onBack,
  onOpenFacility,
  onOpenNetworkFacility,
  networkId = null,
}: Props) {
  const scopeKey =
    networkId != null
      ? `network:${networkId}`
      : selectedFacilityId != null
        ? `facility:${selectedFacilityId}`
        : "none";
  const membershipSignature = useMemo(
    () => buildFqhcMembershipSignature(members),
    [members],
  );
  const requestKey = `${scopeKey}|${membershipSignature}`;
  const [query, setQuery] = useState<FqhcQuery>(() => defaultQuery(scopeKey));
  const activeQuery =
    query.scopeKey === scopeKey ? query : defaultQuery(scopeKey);
  const [loadState, setLoadState] = useState<LiveLoadState>({
    scopeKey: requestKey,
    status: "idle",
  });
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (scopeKey === "none") {
      requestIdRef.current += 1;
      setLoadState({ scopeKey: requestKey, status: "idle" });
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let disposed = false;
    setLoadState({ scopeKey: requestKey, status: "loading" });
    const requestOptions = {
      limit: activeQuery.pageSize,
      offset: activeQuery.page * activeQuery.pageSize,
      sort: activeQuery.sort,
      direction: activeQuery.direction,
    };
    const request =
      networkId != null
        ? getNetworkFqhcForNetwork(networkId, requestOptions)
        : getNetworkFqhc(selectedFacilityId!, requestOptions);
    void request
      .then((response) => {
        if (!disposed && requestIdRef.current === requestId) {
          const lastPage = Math.max(
            0,
            Math.ceil(response.data.total_count / activeQuery.pageSize) - 1,
          );
          if (activeQuery.page > lastPage) {
            setQuery({
              scopeKey,
              page: lastPage,
              pageSize: activeQuery.pageSize,
              sort: activeQuery.sort,
              direction: activeQuery.direction,
            });
            return;
          }
          setLoadState({
            scopeKey: requestKey,
            status: "loaded",
            data: response.data,
            meta: response.meta,
          });
        }
      })
      .catch((error) => {
        if (!disposed && requestIdRef.current === requestId) {
          setLoadState({
            scopeKey: requestKey,
            status: "error",
            message:
              error instanceof Error
                ? error.message
                : "Failed to load FQHC dashboard.",
          });
        }
      });
    return () => {
      disposed = true;
    };
  }, [
    activeQuery.direction,
    activeQuery.page,
    activeQuery.pageSize,
    activeQuery.sort,
    networkId,
    reloadKey,
    requestKey,
    scopeKey,
    selectedFacilityId,
  ]);

  const currentLoadState = useMemo<LiveLoadState>(
    () =>
      loadState.scopeKey === requestKey
        ? loadState
        : { scopeKey: requestKey, status: "loading" },
    [loadState, requestKey],
  );
  const liveModel = useMemo(
    () =>
      currentLoadState.status === "loaded"
        ? buildLiveFqhcDashboardModel(
            currentLoadState.data,
            collectLinkedFqhcFacilities(members, selectedFacilityId),
          )
        : null,
    [currentLoadState, members],
  );
  const loadedMeta =
    currentLoadState.status === "loaded" ? currentLoadState.meta : null;

  const handleSort = useCallback(
    (column: FqhcDashboardSort) => {
      setQuery((current) => {
        const scoped =
          current.scopeKey === scopeKey ? current : defaultQuery(scopeKey);
        return {
          ...scoped,
          page: 0,
          sort: column,
          direction:
            scoped.sort === column && scoped.direction === "asc"
              ? "desc"
              : "asc",
        };
      });
    },
    [scopeKey],
  );

  const sourceIssues =
    currentLoadState.status === "loaded"
      ? Object.entries(currentLoadState.meta.source_statuses ?? {}).filter(
          ([, source]) => source.status !== "current",
        )
      : [];
  const dataWarnings =
    currentLoadState.status === "loaded"
      ? currentLoadState.meta.data_quality_warnings ?? []
      : [];
  const totalCount = liveModel?.facilityCount ?? 0;
  const pageSizeOptions = Array.from(
    new Set([25, 50, 100, Math.min(Math.max(totalCount, 1), 500)]),
  ).sort((a, b) => a - b);
  const rangeStart =
    totalCount === 0 ? 0 : activeQuery.page * activeQuery.pageSize + 1;
  const rangeEnd = Math.min(
    (activeQuery.page + 1) * activeQuery.pageSize,
    totalCount,
  );
  const badgeCount = liveModel?.facilityCount;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <FacilityNetworkBackButton
        label="Back to Networks Overview"
        onClick={onBack}
        variant="link"
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              letterSpacing: FN.figmaTitleTracking,
              lineHeight: "32px",
            }}
          >
            Federally Qualified Health Centers
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "24px",
              bgcolor: FN.badgeBg,
              boxShadow: "0px 2px 2px #e9f3f5",
            }}
          >
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.title,
              }}
            >
              {badgeCount == null
                ? "Loading…"
                : `${badgeCount} Facilit${badgeCount === 1 ? "y" : "ies"}`}
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            color: STATUS_NEUTRAL,
            letterSpacing: "0.03px",
          }}
        >
          {FQHC_DASHBOARD_SUBTITLE}
        </Typography>
      </Box>

      {scopeKey === "none" ? (
        <Alert severity="info">
          Select a facility or network to load FQHC dashboard data.
        </Alert>
      ) : currentLoadState.status === "loading" ||
        currentLoadState.status === "idle" ? (
        <FacilityNetworkFigmaCard>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 3,
            }}
          >
            <CircularProgress size={24} sx={{ color: FN.primary }} />
            <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
              Loading FQHC dashboard…
            </Typography>
          </Box>
        </FacilityNetworkFigmaCard>
      ) : currentLoadState.status === "error" ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setReloadKey((current) => current + 1)}
            >
              Retry
            </Button>
          }
        >
          {currentLoadState.message}
        </Alert>
      ) : liveModel && loadedMeta ? (
        <>
          {loadedMeta?.last_pipeline_refresh_date ? (
            <Typography sx={{ fontSize: 12, color: STATUS_NEUTRAL }}>
              Pipeline refresh: {loadedMeta.last_pipeline_refresh_date}
            </Typography>
          ) : null}
          {sourceIssues.length ? (
            <Alert severity="warning">
              {sourceIssues.map(([sourceName, source]) => (
                <Typography key={sourceName} component="div" sx={{ fontSize: 12 }}>
                  {humanizeSourceName(sourceName)}: {source.status}
                  {source.error_summary
                    ? ` — ${source.error_summary}`
                    : ""}
                </Typography>
              ))}
            </Alert>
          ) : null}
          {dataWarnings.length ? (
            <Alert severity="warning">
              {dataWarnings.map((warning) => (
                <Typography key={warning} component="div" sx={{ fontSize: 12 }}>
                  {warning}
                </Typography>
              ))}
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
            {liveModel.kpis.map((tile) => (
              <KpiTile
                key={tile.id}
                tile={tile}
                sourceTooltip={buildFqhcSourceTooltip(
                  loadedMeta,
                  KPI_SOURCES[tile.id] ?? [],
                )}
              />
            ))}
          </Box>
          {liveModel.facilityCount === 0 && liveModel.roster.length === 0 ? (
            <FacilityNetworkFigmaCard>
              <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
                No {FQHC_TYPE_CODE}s found in this portfolio.
              </Typography>
            </FacilityNetworkFigmaCard>
          ) : (
            <FacilityNetworkFigmaCard padding={0} gap={0}>
              <Box sx={{ p: 2, borderBottom: "1px solid #e5e5e5" }}>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 600,
                    color: STATUS_NEUTRAL,
                  }}
                >
                  Facility roster
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: STATUS_NEUTRAL,
                    mt: 0.5,
                  }}
                >
                  {FQHC_ROSTER_SUBTITLE}
                </Typography>
              </Box>
              <LiveRosterTable
                rows={liveModel.roster}
                sort={activeQuery.sort}
                direction={activeQuery.direction}
                onSort={handleSort}
                meta={loadedMeta}
                onOpenFacility={onOpenFacility}
                onOpenNetworkFacility={onOpenNetworkFacility}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  p: 2,
                }}
              >
                <Typography sx={{ fontSize: 12, color: STATUS_NEUTRAL }}>
                  Showing {rangeStart}–{rangeEnd} of {totalCount}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 12, color: STATUS_NEUTRAL }}>
                    Rows
                  </Typography>
                  <Select
                    size="small"
                    value={activeQuery.pageSize}
                    onChange={(event: SelectChangeEvent<number>) => {
                      const pageSize = Number(event.target.value);
                      setQuery({
                        ...activeQuery,
                        scopeKey,
                        page: 0,
                        pageSize,
                      });
                    }}
                    inputProps={{ "aria-label": "Rows per page" }}
                  >
                    {pageSizeOptions.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size === totalCount && totalCount <= 500
                          ? `Show all (${size})`
                          : size === 500 && totalCount > 500
                            ? "Show 500 (maximum)"
                            : size}
                      </MenuItem>
                    ))}
                  </Select>
                  <Pagination
                    count={Math.max(
                      1,
                      Math.ceil(totalCount / activeQuery.pageSize),
                    )}
                    page={activeQuery.page + 1}
                    onChange={(_, page) => {
                      setQuery({
                        ...activeQuery,
                        scopeKey,
                        page: page - 1,
                      });
                    }}
                    size="small"
                    color="primary"
                  />
                </Box>
              </Box>
            </FacilityNetworkFigmaCard>
          )}
        </>
      ) : null}
    </Box>
  );
}
